import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Package, Plus, Calendar, Check, Trash2, RotateCcw, Search, Clock, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}
function daysFromNow(s) {
  if (!s) return null;
  const d = new Date(s).getTime();
  const now = Date.now();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

const STATUS_META = {
  active:   { label: "Active",   bg: "bg-emerald-500/10",  text: "text-emerald-600 dark:text-emerald-300", border: "border-emerald-500/20", icon: Clock },
  overdue:  { label: "Overdue",  bg: "bg-red-500/10",      text: "text-red-600 dark:text-red-300",         border: "border-red-500/20",     icon: AlertTriangle },
  returned: { label: "Returned", bg: "bg-slate-500/10",    text: "text-muted-foreground",                  border: "border-slate-500/15",   icon: Check },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.active;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.bg} ${m.text} ${m.border} border`} data-testid={`loan-status-${status}`}>
      <Icon className="h-3 w-3" />
      {m.label}
    </span>
  );
}


export default function EquipmentLoansPanel({ onDataChange }) {
  const [members, setMembers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | overdue | returned | all
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);          // {loan} for edit modal
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        api.get("/members"),
        api.get("/equipment-loans?status=all"),
      ]);
      setMembers(m.data || []);
      setLoans(l.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load loans");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = loans;
    if (filter !== "all") list = list.filter((x) => x.status === filter);
    const t = q.trim().toLowerCase();
    if (t) {
      list = list.filter((x) =>
        `${x.user_name || ""} ${x.user_member_id || ""} ${x.items || ""}`.toLowerCase().includes(t)
      );
    }
    return list;
  }, [loans, filter, q]);

  const counts = useMemo(() => {
    const c = { active: 0, overdue: 0, returned: 0, all: loans.length };
    loans.forEach((l) => { c[l.status] = (c[l.status] || 0) + 1; });
    return c;
  }, [loans]);

  const onSaved = (loan) => {
    setLoans((arr) => {
      const idx = arr.findIndex((x) => x.id === loan.id);
      if (idx === -1) return [loan, ...arr];
      const copy = [...arr];
      copy[idx] = loan;
      return copy;
    });
  };

  const markReturned = async (loan) => {
    try {
      const { data } = await api.post(`/equipment-loans/${loan.id}/return`);
      onSaved(data);
      toast.success(`Marked returned: ${loan.user_name}`);
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark returned");
    }
  };
  const reopen = async (loan) => {
    try {
      const { data } = await api.post(`/equipment-loans/${loan.id}/reopen`);
      onSaved(data);
      toast.success("Loan re-opened");
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-open");
    }
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/equipment-loans/${confirmDelete.id}`);
      setLoans((arr) => arr.filter((x) => x.id !== confirmDelete.id));
      setConfirmDelete(null);
      toast.success("Loan deleted");
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="equipment-loans-panel">
      {/* Toolbar */}
      <Card className="p-4 rounded-2xl">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center">
              <Package className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold">Equipment Loans</div>
              <div className="text-[11px] text-muted-foreground">
                Track items loaned to members. Overdue loans alert the member on their dashboard until returned or extended.
              </div>
            </div>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-9 pl-9 rounded-lg w-full md:w-64"
              placeholder="Search by member or item…"
              data-testid="loans-search"
            />
          </div>
          <Button className="rounded-full" onClick={() => setCreateOpen(true)} data-testid="loan-create-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New loan
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {[
            { k: "active",   label: "Active" },
            { k: "overdue",  label: "Overdue" },
            { k: "returned", label: "Returned" },
            { k: "all",      label: "All" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              className={
                `inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border transition-colors ${
                  filter === t.k
                    ? "bg-[hsl(var(--fg))] text-[hsl(var(--bg))] border-[hsl(var(--fg))]"
                    : "border-border hover:bg-[hsl(var(--secondary))]"
                }`
              }
              data-testid={`loans-filter-${t.k}`}
            >
              {t.label}
              <span className="opacity-70">{counts[t.k] ?? 0}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* List */}
      <Card className="p-0 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm font-medium">No {filter !== "all" ? filter : ""} loans</p>
            <p className="text-xs text-muted-foreground mt-1">Create one with the &ldquo;New loan&rdquo; button above.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((ln) => {
              const days = daysFromNow(ln.due_date);
              return (
                <div key={ln.id} className="p-4 hover:bg-[hsl(var(--secondary))/0.4]" data-testid={`loan-row-${ln.id}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--bg))] flex items-center justify-center text-sm font-semibold shrink-0">
                      {(ln.user_name?.[0] || "?").toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="text-sm font-medium">{ln.user_name}</span>
                        <span className="text-[11px] text-muted-foreground">{ln.user_member_id}</span>
                        <StatusBadge status={ln.status} />
                      </div>
                      <div className="text-sm mt-1 whitespace-pre-wrap" data-testid={`loan-items-${ln.id}`}>{ln.items}</div>
                      <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due {fmtDate(ln.due_date)}
                          {ln.status === "active" && days !== null && (
                            <span className="ml-1">({days >= 0 ? `in ${days} day${days === 1 ? "" : "s"}` : "overdue"})</span>
                          )}
                          {ln.status === "overdue" && days !== null && (
                            <span className="ml-1 text-red-500">({Math.abs(days)} day{Math.abs(days) === 1 ? "" : "s"} overdue)</span>
                          )}
                        </span>
                        {ln.returned_at && (
                          <span className="inline-flex items-center gap-1">
                            <Check className="h-3 w-3" /> Returned {fmtDate(ln.returned_at)}
                          </span>
                        )}
                        {ln.last_extended_at && !ln.returned_at && (
                          <span className="inline-flex items-center gap-1">
                            <RotateCcw className="h-3 w-3" /> Extended {fmtDate(ln.last_extended_at)}
                          </span>
                        )}
                      </div>
                      {ln.notes && (
                        <div className="text-[11px] text-muted-foreground italic mt-1">Notes: {ln.notes}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {ln.status !== "returned" ? (
                        <Button size="sm" variant="outline" className="h-8 px-3 text-xs rounded-full" onClick={() => markReturned(ln)} data-testid={`loan-return-${ln.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Returned
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-8 px-3 text-xs" onClick={() => reopen(ln)} data-testid={`loan-reopen-${ln.id}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Re-open
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-8 px-3 text-xs" onClick={() => setEditing(ln)} data-testid={`loan-edit-${ln.id}`}>
                        Edit
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setConfirmDelete(ln)} aria-label="Delete" data-testid={`loan-delete-${ln.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <LoanDialog
        open={createOpen || !!editing}
        loan={editing}
        members={members}
        onOpenChange={(v) => { if (!v) { setCreateOpen(false); setEditing(null); } }}
        onSaved={(loan) => { onSaved(loan); setCreateOpen(false); setEditing(null); }}
        onDataChange={onDataChange}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this loan record?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && `${confirmDelete.user_name} — ${confirmDelete.items}`}
              <br />This permanently removes the record. To preserve history, mark the loan as &ldquo;Returned&rdquo; instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function LoanDialog({ open, loan, members, onOpenChange, onSaved, onDataChange }) {
  const isEdit = !!loan;
  const [userId, setUserId] = useState("");
  const [items, setItems] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (loan) {
      setUserId(loan.user_id);
      setItems(loan.items || "");
      setDueDate((loan.due_date || "").slice(0, 10));
      setNotes(loan.notes || "");
    } else {
      setUserId("");
      setItems("");
      // Default new loans to a fortnight
      const d = new Date(); d.setDate(d.getDate() + 14);
      setDueDate(d.toISOString().slice(0, 10));
      setNotes("");
    }
  }, [open, loan]);

  const submit = async () => {
    if (!isEdit && !userId) { toast.error("Choose a member"); return; }
    if (!items.trim()) { toast.error("Describe the item(s)"); return; }
    if (!dueDate) { toast.error("Set a due date"); return; }
    setSaving(true);
    try {
      let resp;
      if (isEdit) {
        resp = await api.put(`/equipment-loans/${loan.id}`, { items, due_date: dueDate, notes });
      } else {
        resp = await api.post("/equipment-loans", { user_id: userId, items, due_date: dueDate, notes });
      }
      onSaved(resp.data);
      toast.success(isEdit ? "Loan updated" : "Loan created");
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="loan-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit equipment loan" : "New equipment loan"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the items, extend the due date, or leave notes."
              : "Select a member, describe the items, and set when they are due back."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Member</Label>
            {isEdit ? (
              <div className="mt-1 text-sm px-3 py-2 rounded-md border border-border bg-[hsl(var(--secondary))]" data-testid="loan-dialog-member-readonly">
                {loan.user_name} <span className="text-muted-foreground">({loan.user_member_id})</span>
              </div>
            ) : (
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger data-testid="loan-dialog-member"><SelectValue placeholder="Select member…" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`loan-dialog-member-${m.member_id}`}>
                      {m.first_name} {m.last_name} — {m.member_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs">Item(s) on loan</Label>
            <Textarea
              rows={3}
              value={items}
              onChange={(e) => setItems(e.target.value)}
              placeholder="e.g. 1x Kookaburra bat, batting pads, helmet, gloves"
              data-testid="loan-dialog-items"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Due back by</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="loan-dialog-due" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Where they were issued, etc." data-testid="loan-dialog-notes" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="loan-dialog-save">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create loan")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
